"use client";

import { useMemo, useState } from "react";
import { ArrowRight, MapPin, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
interface Community {
  name: string;
  description: string;
}
const FREDERICK_COMMUNITIES: Community[] = [{
  name: "Frederick City",
  description: "Historic downtown along the Monocacy River"
}, {
  name: "Urbana",
  description: "Rapidly growing corridor with modern residential & commercial hubs"
}, {
  name: "Walkersville",
  description: "Charming heritage community northeast of Frederick"
}, {
  name: "Brunswick",
  description: "Historic railroad hub bordering the Potomac River"
}, {
  name: "Middletown",
  description: "Picturesque Middletown Valley agricultural & residential center"
}, {
  name: "Mount Airy",
  description: "Four-county junction community with thriving neighborhoods"
}, {
  name: "New Market",
  description: "Historic National Pike district and residential developments"
}, {
  name: "Thurmont",
  description: "Gateway to Catoctin Mountain and northern rural properties"
}];
export default function FrederickCommunities() {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredCommunities = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return FREDERICK_COMMUNITIES;
    return FREDERICK_COMMUNITIES.filter(community => community.name.toLowerCase().includes(query));
  }, [searchQuery]);
  return <section id="frederickcountycommunities" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Cities & Communities We Serve in Frederick County
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Direct dispatch and rapid emergency restoration response across local Frederick County neighborhoods.
          </p>

          {/* Interactive Search Bar */}
          <div className="mt-8 relative max-w-md mx-auto">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
            <Input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search your city..." aria-label="Search your city in Frederick County" className="h-[46px] pl-10 pr-4 text-sm sm:text-base bg-card border-border rounded-lg shadow-2xs focus-visible:ring-2 focus-visible:ring-primary" />
          </div>
        </div>

        {/* Communities Grid */}
        {filteredCommunities.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {filteredCommunities.map((community, i) => <Card key={community.name} data-index={i} className="bg-card text-card-foreground border-border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardHeader className="gap-2 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 rounded-md bg-muted text-primary inline-flex shrink-0">
                      <MapPin className="size-4" aria-hidden="true" />
                    </span>
                    <CardTitle className="text-lg sm:text-xl font-semibold text-foreground leading-snug">
                      {community.name}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {community.description}
                  </p>
                </CardContent>
              </Card>)}
          </div> : <div className="text-center py-12 px-4 rounded-lg border border-dashed border-border bg-muted/30">
            <p className="text-muted-foreground text-base">
              No communities found matching &ldquo;{searchQuery}&rdquo;.
            </p>
          </div>}

        {/* Emergency CTA Bar */}
        <div className="mt-12 sm:mt-16 p-6 sm:p-8 bg-muted/50 border border-border rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-base sm:text-lg font-medium text-foreground">
            Need emergency restoration services?
          </p>
          <Button asChild size="lg" className="w-full sm:w-auto shrink-0 shadow-sm">
            <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
              <span>Request Emergency Service</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}